import { Spin, Tag, Tooltip } from "antd";
import DeleteThinIcon from "components/Icons/DeleteThinIcon";
import DownloadIcon from "components/Icons/Download";
import EditDropIcon from "components/Icons/EditDropIcon";
import Lock from "components/Icons/Lock";
import { TableCardStyle } from "components/TableCard/TableCard.style";
import Email from "components/Icons/Email";
import ResendIcon from "components/Icons/Resend";
import RefreshIcon from "components/Icons/Refresh";
import Readmail from "components/Icons/Readmail";
import { getFaxStatusColor } from "components/Helper/faxStatusColor";
import { Button } from "components/ui-component";

export interface FaxCardDataType {
  from_number: string;
  to_number: string;
  from_name?: string;
  to_name?: string;
  direction: string;
  status: string;
  pages: number;
  created_on: string;
  viewed: string;
  uuid: string;
  notes?: string | null;
  download_link?: string;
  friendly_name?: string;
  fax_log?: any;
  key?: string;
}

interface CommonTableCardProps {
  faxCardData: FaxCardDataType[];
  onDownloadFax: (fax: FaxCardDataType, downloadLink: string) => void;
  setSentFaxData: (fax: FaxCardDataType) => void;
  setDeleteId: (id: string) => void;
  isLoading: boolean;
}

interface SentFaxCardProps extends CommonTableCardProps {
  tabKeyName: "sent-fax";
  resendFax: (id: string) => void;
}

interface InboxFaxCardProps extends CommonTableCardProps {
  tabKeyName: "inbox";
  resendFax?: never;
}

type TableCardProps = SentFaxCardProps | InboxFaxCardProps;

const TableCard = ({
  faxCardData,
  onDownloadFax,
  setSentFaxData,
  setDeleteId,
  resendFax,
  tabKeyName,
  isLoading,
}: TableCardProps) => {
  return (
    <>
      {isLoading ? (
        <Spin className="loading-spinner" />
      ) : (
        faxCardData?.length > 0 &&
        faxCardData?.map((fax) => (
          <TableCardStyle key={fax?.uuid}>
            <div className="d-flex font-14 justify-content-between align-item-center">
              <div className="to-number">To: {fax?.to_number}</div>
              <div className="icon-wrapper d-flex gap-3">
                {tabKeyName === "inbox" && fax?.viewed === "yes" ? (
                  <Tooltip title="Read">
                    <span>
                      <Readmail />
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Unread">
                    <span>
                      <Email />
                    </span>
                  </Tooltip>
                )}

                <Tooltip title="To ensure the utmost security and confidentiality, all faxes are individually encrypted using a tabKeyName Management Service">
                  <span>
                    <Lock className="lock" />
                  </span>
                </Tooltip>

                {tabKeyName === "sent-fax" && (
                  <Tag bordered={false} color={getFaxStatusColor(fax?.status)}>
                    <span className="capitalize">{fax?.status}</span>
                  </Tag>
                )}
                {tabKeyName === "sent-fax" && fax?.status === "failed" && (
                  <Button
                    type="text"
                    className="refresh-icon"
                    onClick={() => {
                      resendFax(fax?.uuid);
                    }}
                    icon={
                      <Tooltip title="Retry">
                        <span>
                          <RefreshIcon />
                        </span>
                      </Tooltip>
                    }
                  />
                )}
              </div>
            </div>
            <div className="d-flex font-14 justify-content-between align-item-center">
              <div className="from-number">From: {fax?.from_number}</div>
              <div className="date">{fax?.created_on}</div>
            </div>
            <div className="d-flex font-14 justify-content-between align-item-center">
              <div className="notes">
                {fax?.notes ?? "No notes for this fax."}
              </div>
            </div>
            <div className="d-flex font-14 justify-content-between align-item-center">
              {fax?.download_link && (
                <Button
                  type="text"
                  className="d-flex gap-2 download black-btntext font-14"
                  onClick={() => {
                    onDownloadFax(fax, fax.download_link as string);
                  }}
                  icon={
                    <Tooltip title="Download Fax">
                      <span>
                        <DownloadIcon />
                      </span>
                    </Tooltip>
                  }
                >
                  Download
                </Button>
              )}
              <Button
                type="text"
                className="d-flex gap-2 edit black-btntext font-14"
                onClick={() => {
                  setSentFaxData(fax);
                }}
              >
                <EditDropIcon />
                Edit
              </Button>
              {tabKeyName === "sent-fax" && (
                <Button
                  type="text"
                  className="d-flex gap-2 edit black-btntext font-14"
                  onClick={() => {
                    resendFax(fax?.uuid);
                  }}
                >
                  <ResendIcon />
                  Resend
                </Button>
              )}

              {(tabKeyName === "inbox" && fax?.status !== "incomplete") ||
              (tabKeyName === "sent-fax" && fax?.status !== "deleted") ? (
                <Button
                  type="text"
                  className="d-flex gap-2 delete font-14"
                  onClick={() => {
                    setDeleteId(fax?.uuid);
                  }}
                >
                  <DeleteThinIcon width="14" height="14" strokeWidth="2" />
                  Delete
                </Button>
              ) : null}
            </div>
          </TableCardStyle>
        ))
      )}
    </>
  );
};

export default TableCard;
